/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import RouteErrorBoundary from '../../../components/error/RouteErrorBoundary';
import { AppRouteInterface, RouteInterface, RoutesInterface } from '../../../types/routes';
import { Routes, Route, useNavigate } from 'react-router-dom-v5-compat';
import { AppLayout } from '../../../layout/AppLayout';
import Logo from '../../../images/Materiality_Assessment_logo.svg';
import Header from '../../../components/header';
import { AppHeader } from '../../../components/header/AppHeader';
import { TestingToolRoute } from './testing-tool/TestingToolRoute';
import { AssessmentRoute } from './assessment/AssessmentRoute';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { useEffect } from 'react';
import { materialityAssessmentRouteMatcher } from '../../../routes/appRootMatcher';
import { loadInitiativeById } from '../../../actions/initiative';
import { SettingStorage } from '../../../services/SettingStorage';
import Loader from '../../../components/loader';
import { V6Route } from '../../common/types/V6Router';
import { getV6Router } from '../../common/utils/V6Router';
import { useParams } from 'react-router-dom';
import UtrModal from '../../../components/utr-modal';

export const materialityAssessmentRoutes: RoutesInterface = {
  MATERIALITY_ASSESSMENT: {
    id: V6Route.MaterialityAssessmentRoot,
    label: 'Materiality Assessment',
    path: '/materiality-assessment/:initiativeId?',
    icon: 'fa-folder-open',
    component: MaterialityAssessmentRoute,
    exact: false,
    auth: true,
  },
};

export const materialityAssessmentRouteEntry: RouteInterface = {
  ...materialityAssessmentRoutes.MATERIALITY_ASSESSMENT,
};

const materialityAssessmentAppRoute: AppRouteInterface = {
  appName: 'Materiality Assessment',
  appIcon: Logo,
  routes: [materialityAssessmentRoutes.MATERIALITY_ASSESSMENT],
};

const router = getV6Router();
const routeMatcher = materialityAssessmentRouteMatcher;

function MaterialityAssessmentRoute() {
  // above this level we are still using the old router, so need old useParams
  const { initiativeId } = useParams<{ initiativeId?: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: rootInitiatives } = useAppSelector((state) => state.rootInitiatives);
  const organizations = routeMatcher.filterInitiatives(rootInitiatives);
  const selectedId = SettingStorage.getItem(routeMatcher.storageKey) ?? organizations[0]?._id;
  const organization = organizations.find((o) => o._id === selectedId);

  useEffect(() => {
    if (initiativeId) {
      dispatch(loadInitiativeById(initiativeId));
    }
  }, [initiativeId, dispatch]);

  useEffect(() => {
    if (!organization || initiativeId) {
      return;
    }
    // In no organization is selected, redirect to the first one
    const url = router.generateUrl(V6Route.MaterialityAssessmentSurveysList, {
      initiativeId: organization._id,
    });

    navigate(url);
  }, [organization, initiativeId, navigate]);

  return (
    <RouteErrorBoundary>
      <AppLayout
        Header={
          <Header>
            <AppHeader
              appRoute={materialityAssessmentAppRoute}
              route={materialityAssessmentRoutes.MATERIALITY_ASSESSMENT}
            />
          </Header>
        }
      >
        <Routes>
          <Route index element={<Loader />} />
          <Route path={router.getRoute(V6Route.MaterialityAssessmentTestingTool).path} element={<TestingToolRoute />} />
          <Route
            path={router.getRoute(V6Route.MaterialityAssessmentSurveyQuestionIndex).path}
            element={<AssessmentRoute />}
          />
          <Route
            path={router.getRoute(V6Route.MaterialityAssessmentSurveyQuestion).path}
            element={<AssessmentRoute />}
          />
          <Route path={router.getRoute(V6Route.MaterialityAssessmentSurvey).path} element={<AssessmentRoute />} />
          <Route path={router.getRoute(V6Route.MaterialityAssessmentSurveysList).path} element={<AssessmentRoute />} />
          <Route path={router.getRoute(V6Route.MaterialityAssessmentInitiative).path} element={<AssessmentRoute />} />
        </Routes>
      </AppLayout>
      <UtrModal />
    </RouteErrorBoundary>
  );
}
